import { Loader2, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSalonConfig } from "@/domains/settings/features/salon-config/hooks/use-salon-config";
import type { Service } from "@/gql/graphql";
import { cn } from "@/lib/utils";
import { useServicesStep } from "../../../hooks/use-services-step";
import { ServiceWorkerAssignment, type Worker } from "./service-worker-assignment";

interface MobileServicesStepProps {
  servicesStep: ReturnType<typeof useServicesStep>;
  loading?: boolean;
}

export function MobileServicesStep({ servicesStep, loading = false }: MobileServicesStepProps) {
  const { t } = useTranslation("appointments");
  const { config } = useSalonConfig();
  const {
    selectedServices,
    setSelectedServices,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    categories,
    filteredServices,
    handleServiceToggle,
    removeService,
    isServiceSelected,
    workers,
    loading: servicesLoading,
  } = servicesStep;

  return (
    <div
      className={`${config.appointmentWorkerAssignmentEnabled ? "space-y-4" : "flex flex-col h-full"}`}
    >
      <div className={config.appointmentWorkerAssignmentEnabled ? "" : "flex-shrink-0"}>
        {/* Selected Services Summary */}
        <div className="space-y-3 mb-4">
          <div className="flex flex-wrap gap-1 min-h-[28px] items-start">
            {selectedServices.length > 0 ? (
              selectedServices.map((service) => (
                <Badge
                  key={service.id}
                  variant="secondary"
                  className="text-xs flex items-center gap-1"
                >
                  {service.name} - <PriceDisplay amount={service.price} />
                  <button
                    type="button"
                    onClick={() => removeService(service.id)}
                    className="ml-1 hover:bg-accent rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-xs text-muted-foreground py-1">
                {t("booking.servicesStep.labels.selectAtLeastOne")}
              </span>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-3 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder={t("booking.servicesStep.labels.searchServices")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Services List */}
        <div
          className={`border rounded-lg overflow-y-auto bg-card scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent ${
            config.appointmentWorkerAssignmentEnabled ? "max-h-64" : "flex-1 min-h-0"
          }`}
        >
          {servicesLoading ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm">{t("booking.servicesStep.labels.loadingServices")}</span>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center p-6 text-muted-foreground text-sm">
              {searchTerm || selectedCategory !== t("booking.servicesStep.categories.all")
                ? t("booking.servicesStep.messages.noServicesFound")
                : t("booking.servicesStep.messages.noServicesAvailable")}
            </div>
          ) : (
            <div className="divide-y">
              {filteredServices.map((service: Service) => {
                const isSelected = isServiceSelected(service.id);
                return (
                  <div
                    key={service.id}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    className={cn(
                      "w-full text-left p-3 active:bg-accent cursor-pointer transition-colors",
                      isSelected && "bg-accent border-l-4 border-l-primary"
                    )}
                    onClick={() => handleServiceToggle(service)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleServiceToggle(service);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleServiceToggle(service)}
                          className="pointer-events-none"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{service.name}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                            <PriceDisplay amount={service.basePrice ?? 0} />
                          </div>
                          {service.category?.name && (
                            <Badge variant="outline" className="text-xs mt-1">
                              {service.category.name}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Worker Assignment */}
        {config.appointmentWorkerAssignmentEnabled && selectedServices.length > 0 && (
          <div className="mt-8">
            <ServiceWorkerAssignment
              selectedServices={selectedServices}
              workers={workers as unknown as Worker[]}
              onServicesChange={setSelectedServices}
              loading={loading || servicesLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
